import type { IconDefinition } from '@hg-akademie-ban/ban-elevate-assets';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

export type IconSize = 'small' | 'default' | 'large' | 'extralarge';

// These are the sizes defined in the Figma design system, which map to specific size tokens in the codebase (e.g. size-16, size-24, size-32).
// We are adding padding around the icons because this is how it is specified in Figma.
// Most icons in the design system are either 14x14 or 22x22, which with a 1px padding on all sides results in 16x16 and 24x24 respectively.
const sizeClasses: Record<IconSize, string> = {
  small: 'size-16 p-1', // 16×16 box with 1px padding → 14×14 visual icon (matches Figma)
  default: 'size-24 p-1', // 24×24 box with 1px padding → 22×22 visual icon (matches Figma)
  large: 'size-32', // 32x32 without padding → 32×32 visual icon
  extralarge: 'size-48', // 48x48 without padding → 48×48 visual icon
};

export type IconProps = ComponentProps<'svg'> & {
  children?: never;
  icon: IconDefinition;
  size?: IconSize;
  /** Tailwind class for the secondary (background) duotone layer (e.g. `"text-accent"`). Sets `color` so `fill="currentColor"` resolves to it. When set, opacity defaults to `1`. */
  secondaryClassName?: string;
  /** Opacity for the secondary duotone layer (0–1). Defaults to `0.25`, or `1` when `secondaryColor`/`secondaryClassName` is set. */
  secondaryOpacity?: number;
};

/**
 * Native SVG icon component that renders FontAwesome IconDefinition
 * without FontAwesome's runtime CSS injection.
 *
 * Duotone icons support two-color rendering via `primaryClassName` and `secondaryClassName` props.
 * Pass Tailwind class names to colorize each layer independently.
 *
 * @example
 * ```tsx
 * import { faCircleInfo } from '@hg-akademie-ban/ban-elevate-assets/icons/regular';
 * <Icon icon={faCircleInfo} />
 *
 * // Duotone with Tailwind classes
 * import { faHouse } from '@hg-akademie-ban/ban-elevate-assets/icons/duotone';
 * <Icon icon={faHouse} secondaryClassName="text-accent" />
 *
 * // Duotone with explicit CSS color values
 * <Icon icon={faHouse} primaryColor="var(--ld-color-content-brand)" secondaryColor="var(--ld-color-content-accent)" />
 * ```
 */
export function Icon({ icon, size = 'default', className, secondaryClassName, secondaryOpacity, ...props }: IconProps) {
  const width = icon.icon[0];
  const height = icon.icon[1];
  const pathData = icon.icon[4];
  const sizeClass = sizeClasses[size];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${width} ${height}`}
      fill="currentColor"
      aria-hidden="true"
      data-slot="icon"
      className={cn('pointer-events-none inline-block shrink-0', sizeClass, className)}
      {...props}
    >
      {Array.isArray(pathData) ? (
        // Duotone icons have two paths (https://docs.fontawesome.com/web/style/duotone/#changing-opacity)
        <>
          <path
            key="secondary"
            fill="currentColor"
            className={secondaryClassName}
            d={pathData[0]}
            opacity={secondaryOpacity ?? (secondaryClassName ? 1 : 0.25)}
          />
          <path key="primary" fill="currentColor" d={pathData[1]} />
        </>
      ) : (
        <path fill="currentColor" d={pathData} />
      )}
    </svg>
  );
}
